import { BottomSheetView } from '@gorhom/bottom-sheet';
import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/src/types';
import React, { RefObject, useCallback, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BrazeCardBase } from 'services/engagement/braze/types';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import Illustration, {
  IllustrationKeyType,
} from 'shared/components/vxl/Illustration/Illustration';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isWeb } from 'shared/constants/platform';
import { useEngagement } from 'shared/hooks/useBraze';
import theme from 'shared/styles/theme';

export interface BrazeBottomSheetProps extends BrazeCardBase {
  bottomSheetRef?: RefObject<BottomSheetModalMethods | null>;
  id: string;
  illustrationKey: IllustrationKeyType;
  title: string;
  subtext: string;
  disclosureTagline: string;
  disclosureLinkText: string;
  disclosureText: string;
  primaryButtonLabel: string;
  url?: string;
}

const DisclosureContent = ({
  disclosureText,
  onDismiss,
}: {
  disclosureText: string;
  onDismiss: () => void;
}) => {
  return (
    <View style={styles.disclosureContainer}>
      <BodyBaseText>{disclosureText}</BodyBaseText>
      <Button label="Dismiss" onPress={onDismiss} />
    </View>
  );
};

const BrazeBottomSheetContent = (props: BrazeBottomSheetProps) => {
  const { bottom } = useSafeAreaInsets();
  const {
    bottomSheetRef,
    illustrationKey,
    title,
    subtext,
    disclosureLinkText,
    disclosureTagline,
    disclosureText,
    primaryButtonLabel,
  } = props;
  const [displayDisclosure, setDisplayDisclosure] = useState(false);
  const {
    logContentCardClicked,
    logContentCardDismissed,
    clearBottomSheetContent,
  } = useEngagement();

  const toggleDisclosure = useCallback(() => {
    setDisplayDisclosure((prev) => !prev);
  }, []);

  const handlePrimaryButtonPress = () => {
    bottomSheetRef?.current?.dismiss();
    logContentCardClicked(props);
    clearBottomSheetContent?.();
  };

  const handleDismiss = () => {
    logContentCardDismissed(props);
    clearBottomSheetContent?.();
  };

  const content = displayDisclosure ? (
    <DisclosureContent
      disclosureText={disclosureText}
      onDismiss={toggleDisclosure}
    />
  ) : (
    <View
      style={[styles.containerView, { paddingBottom: bottom }]}
      testID="Braze-bottom-sheet"
    >
      <View style={styles.illustrationContainer}>
        <Illustration illustration={illustrationKey} />
      </View>
      <TitleLgText numberOfLines={2}>{title}</TitleLgText>
      <BodyBaseText numberOfLines={3}>{subtext}</BodyBaseText>
      <Text
        onClick={() => {
          toggleDisclosure();
        }}
      >
        <BodyXSText style={styles.disclosureText}>
          {disclosureTagline}
        </BodyXSText>
        <HyperlinkSmText
          onPress={() => {
            toggleDisclosure();
          }}
        >
          {disclosureLinkText}
        </HyperlinkSmText>
      </Text>
      <Button
        label={primaryButtonLabel}
        onPress={() => {
          handlePrimaryButtonPress();
        }}
      />
    </View>
  );

  return (
    <BottomSheet allowScrolling ref={bottomSheetRef} onDismiss={handleDismiss}>
      {isWeb ? content : <BottomSheetView>{content}</BottomSheetView>}
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  containerView: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.m,
    marginBottom: theme.spacing.s,
    minHeight: 300,
    paddingHorizontal: theme.spacing.l,
    flex: 1,
  },
  disclosureContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: 16,
    paddingBottom: 12,
    paddingHorizontal: 24,
  },
  disclosureText: {
    color: theme.color.text.secondary,
  },
  illustrationContainer: {
    height: 72,
    width: 72,
  },
});

export default BrazeBottomSheetContent;
